import { TabsTrigger } from '@radix-ui/react-tabs';
import { ProjectTab } from '@/types';
import BetaBadge from '@/components/ui/BetaBadge';

/**
 * Results tab button. Two visual modes based on viewport width:
 *
 * - **Narrow (< lg, icon-only):** renders as a segmented-control-style
 *   button — padded 36x36 tap target (h-9 w-9), larger icon (size-5),
 *   and a soft neutral pill background when active. The underline is
 *   suppressed at narrow so the pill alone signals the active tab.
 * - **Wide (lg+, icon + label):** renders the original horizontal pill
 *   with an underline below the active tab. The pill background is
 *   suppressed at wide so the underline carries the active signal.
 *
 * The dual treatment keeps the wide layout unchanged while giving icon-
 * only tabs proper visual weight, a proper click target, and a clear
 * active-tab affordance at narrow widths.
 */
function Tab({ value, icon: Icon, label, currentTheme, activeTab, beta }: ProjectTab) {
  const isActive = activeTab === value;

  return (
    <TabsTrigger
      value={value}
      style={{ backgroundColor: currentTheme?.background }}
      className={`!shadow-none transition-all ease-in-out duration-200 relative flex items-center justify-center rounded-md h-9 w-9 lg:w-auto lg:h-auto lg:rounded-none lg:px-4 lg:py-1.5 ${
        isActive
          ? 'bg-neutral-100 lg:!bg-transparent'
          : 'hover:bg-neutral-50 lg:hover:!bg-transparent'
      }`}
      aria-label={beta ? `${label} (Beta)` : label}
      title={beta ? `${label} (Beta)` : label}
    >
      <div className="flex items-center gap-1.5 text-center justify-center">
        <Icon
          style={{ color: isActive ? currentTheme?.primary : undefined }}
          className={`size-5 lg:size-4 transition-colors duration-200 ${isActive ? '' : 'text-neutral-400'}`}
        />
        <span
          className={`hidden lg:inline text-sm transition-colors duration-200 ${
            isActive ? 'font-medium text-neutral-900' : 'text-neutral-500 hover:text-neutral-700'
          }`}
        >
          {label}
        </span>
        {/* Beta badge — only shown at lg+ alongside the text label.
            At narrow widths the tab is icon-only; surfacing the badge
            there would require breaking the 36×36 square footprint of
            the segmented-control button. The in-page Beta badge in
            ChatThread's context bar covers the narrow-width case. */}
        {beta && (
          <span className="hidden lg:inline-flex">
            <BetaBadge size="xs" />
          </span>
        )}
      </div>
      {/* Underline — only visible at lg+, where it is the active-tab
          signal. At narrow the pill background above does that job
          instead, so the underline is hidden. */}
      <span
        className={`hidden lg:block absolute -bottom-3.5 left-0 right-0 h-0.5 rounded-full transition-all duration-200 ${
          isActive ? 'opacity-100 bg-primary' : 'opacity-0 bg-transparent'
        }`}
      />
    </TabsTrigger>
  );
}

export default Tab;
